import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { VideoCameraOff } from '@components/icons'
import { useDaily, useDevices } from '@daily-co/daily-react'
import { trackEvent } from '@lib/clinician-tracking'
import { ChevronLeft } from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import { BlueNavLogo } from '../../../../images/brand/blue-nav-logo'
import { TestAudioButton } from './components'
import { CallSetup } from './components/call-setup'
import { usePatientDailyManager } from './context/patient-daily-manager.context'

export const ClientWaitingRoom = () => {
  const [promptForPermissions, setPromptForPermissions] = useState(false)
  const [isPromptingLoading, setIsPromptingLoading] = useState(false)

  const { participantFullName, setParticipantFullName, setIsReadyToJoin } =
    usePatientDailyManager()

  const daily = useDaily()
  const devices = useDevices()

  useEffect(() => {
    if (daily) {
      daily?.enumerateDevices().then(devices => {
        if (devices.devices.filter(d => !!d.deviceId).length === 0) {
          setPromptForPermissions(true)
        } else {
          daily.startCamera()
        }
      })
    }
  }, [daily])

  const handlePromptPermissions = () => {
    daily?.startCamera()
    setIsPromptingLoading(true)
    setTimeout(() => {
      // wait a second for native prompt to appear
      setPromptForPermissions(false)
      setIsPromptingLoading(false)
    }, 1300)
  }

  const setReadyToJoin = () => {
    setIsReadyToJoin(true)
    trackEvent('click:telehealth-session.participant-ready-to-join')
  }

  const actionBar = (
    <HStack w="100%" gap="8px">
      <TestAudioButton
        currentDevice={devices.currentSpeaker?.device.deviceId || 'default'}
      />
      <Button
        w="100%"
        maxW="50%"
        onClick={setReadyToJoin}
        size="lg"
        data-click-text="Ask to join"
      >
        Ask to join
      </Button>
    </HStack>
  )

  const renderPromptForPermissions = () => {
    return (
      <>
        <Text fontSize="24px" fontWeight="430">
          Hi {participantFullName},
        </Text>
        <VStack gap="xsmall">
          <Text align="left" mt="0px">
            To join a telehealth session, please give us access to your camera
            and microphone.
          </Text>
          <Flex
            borderRadius="12px"
            bg="black"
            justify="center"
            align="center"
            w="100%"
            h="100%"
          >
            <Box
              borderRadius="12px"
              h={{
                base: '200px',
                sm: '275px'
              }}
              w="100%"
              overflow="hidden"
              position="relative"
            >
              <Flex
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                justify="center"
                align="center"
                bg="gray.800"
                borderRadius="12px"
              >
                <VideoCameraOff width="48px" fill="#757575" />
              </Flex>
            </Box>
          </Flex>
          <Button
            onClick={handlePromptPermissions}
            isLoading={isPromptingLoading}
            size="lg"
            mx="0px"
            isFullWidth
            data-click-text="Enable access to camera and mic"
          >
            Enable access to camera and mic
          </Button>
        </VStack>
      </>
    )
  }

  return (
    <Flex
      direction="column"
      h="100%"
      w="100%"
      justifyContent="space-between"
      alignItems="center"
      p="small"
      gap={{
        base: 'medium',
        md: '0px'
      }}
    >
      <Box
        h="46px"
        pl={{
          base: '0px',
          md: 'small'
        }}
        mt="xsmall"
        alignSelf={{
          base: 'center',
          md: 'flex-start'
        }}
      >
        <BlueNavLogo />
      </Box>
      <Flex
        direction="column"
        alignItems="center"
        p="small"
        bg="white"
        borderRadius="16px"
        maxW="512px"
        w="100%"
      >
        <Box
          width="100%"
          bg="white"
          margin="auto"
          borderRadius="12px"
          p="xsmall"
        >
          <Button
            onClick={() => setParticipantFullName('')}
            variant="link"
            fontSize="14px"
            fontWeight="430"
            color="black"
            textDecoration="none"
            m="0px"
            p="0px"
            data-click-text="Check in"
          >
            <ChevronLeft fill="black" />
            Check in
          </Button>
          {promptForPermissions ? (
            renderPromptForPermissions()
          ) : (
            <Box w="100%" h="100%">
              <Box w="100%">
                <Text align="left" fontSize="24px" fontWeight="430">
                  Ready to join?
                </Text>
                <Text
                  align="left"
                  fontSize="16px"
                  fontWeight="430"
                  color="#757575"
                >
                  Check your camera, microphone, and speakers before you join.
                </Text>
              </Box>
              <CallSetup bottomActionBar={actionBar} />
            </Box>
          )}
        </Box>
      </Flex>
      <Box h="46px" />
    </Flex>
  )
}
